import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  ArrowPathIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';
import ConnectionsTable from '../components/ConnectionsTable';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.getClients();
      const clientsData = response.data.clients || {};
      const clientList = Object.values(clientsData);
      
      // Sort clients by created_at date, latest first
      const sortedClients = clientList.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Latest first
      });
      
      setClients(sortedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.getAgents();
      const activeAgents = response.data.agents?.filter(agent => agent.status === 'active') || [];
      setAgents(activeAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load agents');
    }
  };

  const handleClientUpdate = (clientId, updateData) => {
    // Update local state immediately for better UX
    setClients(prevClients => 
      prevClients.map(client => 
        client.client_id === clientId 
          ? { ...client, ...updateData }
          : client
      )
    );
  };

  const handleClientDelete = (clientId) => {
    // Remove client from local state
    setClients(prevClients => 
      prevClients.filter(client => client.client_id !== clientId)
    );
  };

  useEffect(() => {
    fetchClients();
    fetchAgents();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-whatsapp-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">WhatsApp Connections</h1>
          <p className="text-gray-500 mt-1">
            Manage your WhatsApp agent connections. Click on an AI Agent to reassign connections.
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchClients}
            className="btn-secondary btn-sm"
            disabled={loading}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link to="/dashboard/agents/create" className="btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Agent
          </Link>
        </div>
      </div>

      {/* Clients Table */}
      {clients.length === 0 ? (
        <div className="card text-center py-12">
          <UsersIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No connections yet</h3>
          <p className="text-gray-500 mb-6">Get started by creating your first WhatsApp connection</p>
          <Link 
            to="/dashboard/agents/create" 
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-whatsapp-500 to-green-600 text-white font-semibold rounded-xl hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="h-6 w-6 mr-3" />
            Create Your First Agent
          </Link>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <ConnectionsTable 
            clients={clients}
            agents={agents}
            onClientUpdate={handleClientUpdate}
            onClientDelete={handleClientDelete}
            showDeleteAction={true}
          />
        </div>
      )}
    </div>
  );
};

export default ClientList;
